/**
 * Clipboard-paste decisions shared by every `<MarkdownEditor>` surface
 * (card description, card + wiki comments, wiki doc, chat composer).
 *
 * A paste carries up to two representations of the same selection:
 * `text/html` (the rich projection) and `text/plain` (the flat one).
 * Picking the wrong half silently drops formatting the user expects to
 * survive — most visibly inline code, which has no plain-text spelling
 * once the source app has rendered it.
 *
 * The rule here matches what Jira / Confluence / Notion do: the rich
 * half wins whenever it actually carries structure, and the plain half
 * is only re-read as markdown source when the HTML turns out to be a
 * bare wrapper around that same source.
 */

export type PasteStrategy =
  /** Let ProseMirror's own pipeline handle it (rich HTML, or plain-only). */
  | 'default'
  /** Re-read the `text/plain` half as markdown source. */
  | 'markdown'

/**
 * Markup that a markdown projection of the same selection cannot
 * reproduce. If the HTML half contains any of it, that half is the
 * richer one and must be the one we parse.
 *
 * `pre` is deliberately ABSENT, and inline `code` is scoped to
 * `:not(pre code)`: a payload whose entire HTML is one `<pre>` (docs
 * sites, GitHub's blob view, rendered cheat sheets) is a wrapper around
 * SOURCE text, and that source is frequently markdown the user wants
 * rendered rather than re-fenced. Everything inside such a `<pre>` is
 * plain text as far as this decision is concerned.
 */
const RICH_MARKUP_SELECTOR = [
  'code:not(pre code)',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'strike',
  'del',
  'ins',
  'mark',
  'sub',
  'sup',
  'a[href]',
  'ul',
  'ol',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'table',
  'blockquote',
  'img',
  'hr',
].join(',')

/**
 * What KIND of payload is the `text/html` half?
 *
 * - `rich` — carries markup a markdown projection can't reproduce.
 * - `pre`  — the whole selection sits in a `<pre>`. Could be markdown
 *            SOURCE the user wants rendered, or real code they want kept
 *            verbatim; the two are told apart by signal strength below.
 * - `bare` — paragraphs / spans / divs only. Whatever text this carries,
 *            it is definitely not code.
 */
export type HtmlPayloadKind = 'rich' | 'pre' | 'bare'

export function classifyHtmlPayload(html: string): HtmlPayloadKind {
  if (!html.trim()) return 'bare'
  const body = new DOMParser().parseFromString(html, 'text/html').body
  if (!body) return 'bare'
  if (body.querySelector(RICH_MARKUP_SELECTOR)) return 'rich'
  return body.querySelector('pre') ? 'pre' : 'bare'
}

/**
 * Markdown signals that do NOT occur naturally in source code, used to
 * decide whether a `<pre>` holds markdown source or real code.
 *
 * The weak signals are deliberately excluded, because every one of them
 * is ordinary syntax in a shell script or config file: `# ` is a
 * comment, `- ` is a CLI flag or a YAML item, `> ` is a redirect,
 * backticks are command substitution, `1. ` is numbered output. Reading
 * a code block through the markdown parser on the strength of those
 * CORRUPTS it — the comment marker in `# install deps` was consumed and
 * the line promoted to a heading, silently editing the user's code.
 *
 * Erring toward "keep it as code" is the recoverable direction: the user
 * still sees every byte they copied and can convert it by hand, whereas
 * markdown-parsing real code destroys characters with no undo.
 */
function hasStrongMarkdownSignal(text: string): boolean {
  return (
    // Fenced code block — unambiguous markdown, and a nested fence
    // inside real code is vanishingly rare.
    /^```/m.test(text) ||
    // Bold emphasis
    /\*\*[^*\n]+\*\*/.test(text) ||
    /__[^_\n]+__/.test(text) ||
    // Markdown links
    /\[[^\]\n]+\]\([^)\n]+\)/.test(text) ||
    // GFM tables (a row with at least 2 pipes)
    /^\|.*\|.*\|$/m.test(text) ||
    // GFM strikethrough
    /~~[^~\n]+~~/.test(text)
  )
}

/**
 * Heuristic — does this plain-text clipboard payload look like markdown
 * source?
 *
 * Only consulted once the HTML half has been established as a plain-text
 * wrapper, so a false positive costs a markdown re-read of text that had
 * no richer representation to begin with.
 *
 * Patterns covered: ATX + setext headings, bullet + ordered lists,
 * blockquotes, fenced code, bold/italic emphasis, inline code, markdown
 * links, GFM tables, GFM strikethrough.
 */
export function looksLikeMarkdown(text: string): boolean {
  return (
    // ATX headings (`# `, `## `, ...)
    /^#{1,6} \S/m.test(text) ||
    // Setext headings (text followed by `====` or `----` on the next line)
    /^[^\s].*\n=+\s*$/m.test(text) ||
    /^[^\s].*\n-+\s*$/m.test(text) ||
    // Unordered lists
    /^[*+-] \S/m.test(text) ||
    // Ordered lists
    /^\d+\.\s+\S/m.test(text) ||
    // Blockquotes
    /^> /m.test(text) ||
    // Fenced code blocks
    /^```/m.test(text) ||
    // Bold emphasis (`**...**` or `__...__`)
    /\*\*[^*\n]+\*\*/.test(text) ||
    /__[^_\n]+__/.test(text) ||
    // Inline code
    /`[^`\n]+`/.test(text) ||
    // Markdown links
    /\[[^\]\n]+\]\([^)\n]+\)/.test(text) ||
    // GFM tables (a row with at least 2 pipes)
    /^\|.*\|.*\|$/m.test(text) ||
    // GFM strikethrough
    /~~[^~\n]+~~/.test(text)
  )
}

/** Which half of the clipboard payload should be parsed. */
export function decidePasteStrategy(html: string, plain: string): PasteStrategy {
  // Plain-only payloads (terminal copy, Cmd+Shift+V) already flow
  // through tiptap-markdown's `clipboardTextParser`; there is nothing
  // to choose between.
  if (!plain || !html) return 'default'

  switch (classifyHtmlPayload(html)) {
    // The rich half wins whenever it carries structure the plain-text
    // projection cannot. This is the load-bearing clause: a rendered
    // answer pasted out of an AI chat, Notion, or Linear arrives with
    // formatting in `text/html` and a backtick-less flattening in
    // `text/plain`, and re-reading the flat half as markdown throws the
    // code spans away.
    case 'rich':
      return 'default'
    // A `<pre>` is the ambiguous one, and the two readings fail very
    // differently: rendering real code as markdown EDITS it (a `# `
    // comment loses its marker and becomes a heading), while keeping
    // markdown source as a code block loses nothing. So this only leaves
    // the code path for a signal that doesn't occur in code.
    case 'pre':
      return hasStrongMarkdownSignal(plain) ? 'markdown' : 'default'
    // Paragraph / span / div wrappers hold prose, never code, so the
    // looser heuristic is safe here.
    default:
      return looksLikeMarkdown(plain) ? 'markdown' : 'default'
  }
}

/**
 * Rewrite `<h1>`–`<h6>` as a bold paragraph.
 *
 * For a surface whose schema has no heading node (chat), ProseMirror
 * drops the heading and keeps its text as an ordinary paragraph — so a
 * pasted heading lands indistinguishable from body text, and the `##`
 * that carried the intent is consumed on the way. Bold is the closest
 * thing chat CAN express, so the emphasis survives instead of silently
 * evaporating. Inline children (code spans, links) are moved rather than
 * flattened, so they keep their own marks.
 */
export function demoteHeadingsToBold(html: string): string {
  if (!html || !/<h[1-6][\s>]/i.test(html)) return html
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const body = doc.body
  if (!body) return html
  const headings = Array.from(body.querySelectorAll('h1, h2, h3, h4, h5, h6'))
  if (headings.length === 0) return html
  for (const heading of headings) {
    if ((heading.textContent ?? '').trim().length === 0) {
      heading.remove()
      continue
    }
    const paragraph = doc.createElement('p')
    const strong = doc.createElement('strong')
    while (heading.firstChild) strong.append(heading.firstChild)
    paragraph.append(strong)
    heading.replaceWith(paragraph)
  }
  return body.innerHTML
}

/**
 * The same demotion, one layer earlier — on markdown SOURCE text.
 *
 * A plain-text-only paste (a terminal copy, or anything with no
 * `text/html` half) never reaches `transformPastedHTML`: tiptap-markdown
 * parses the text straight to a slice via `clipboardTextParser`, and by
 * the time a heading node exists the schema has already dropped it. So
 * the `#` prefix has to be rewritten before that parse.
 *
 * Fenced regions are tracked and skipped: `# ` is a comment in most
 * shells, and rewriting one inside a code fence would corrupt the code —
 * the same failure this module's `<pre>` rule exists to prevent.
 */
export function boldMarkdownHeadings(text: string): string {
  if (!text.includes('#')) return text
  let insideFence = false
  return text
    .split('\n')
    .map((line) => {
      if (/^\s*(```|~~~)/.test(line)) {
        insideFence = !insideFence
        return line
      }
      if (insideFence) return line
      // No leading whitespace allowed: an indented `#` line is an
      // indented code block, not a heading.
      const match = /^(#{1,6})\s+(.*\S)\s*$/.exec(line)
      if (!match?.[2]) return line
      const content = match[2]
      // Already bold — don't nest a second pair of asterisks.
      return /^\*\*[\s\S]*\*\*$/.test(content) ? content : `**${content}**`
    })
    .join('\n')
}

/** Cell delimiter used when a table has to be flattened to text. */
const CELL_SEPARATOR = ' | '

/**
 * Flatten `<table>` markup to one paragraph per table, with rows
 * separated by `<br>` and cells by ` | `.
 *
 * For surfaces whose schema has no table nodes (chat, wiki comments),
 * ProseMirror drops the table elements and keeps only their text — and
 * because every cell collapses into one run, a pasted table arrives as
 * `EnvParameterDev/IridiumDrupal/Dev/...`: no data lost, but unreadable.
 * Rendering the rows ourselves keeps the grid legible in the one
 * dimension a schema without tables can still express.
 *
 * Cell CONTENT is moved rather than flattened to text, so inline marks
 * inside a cell (notably `<code>`) survive. Rows are joined with `<br>`
 * (a hardBreak — present in every surface tier) rather than one
 * paragraph per row, because paragraph siblings in chat are separated by
 * a blank line and a 3-row table would render double-spaced.
 */
export function flattenTablesToParagraphs(html: string): string {
  if (!html || !/<table/i.test(html)) return html
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const body = doc.body
  if (!body) return html
  // Document order lists an outer table before an inner one, so reversing
  // flattens innermost-first and a nested table is already plain by the
  // time its parent is processed.
  const tables = Array.from(body.querySelectorAll('table')).reverse()
  if (tables.length === 0) return html

  for (const table of tables) flattenOneTable(doc, table)
  return body.innerHTML
}

/**
 * Flatten one `<table>` in place: its rows become `cell | cell` runs
 * separated by `<br>`, all inside a single replacement `<p>`.
 *
 * Cell CONTENT is moved rather than read as text, so inline marks inside
 * a cell (notably `<code>`) survive the flattening. A table whose cells
 * are all empty is removed outright rather than leaving a blank
 * paragraph behind.
 */
function flattenOneTable(doc: Document, table: Element): void {
  const paragraph = doc.createElement('p')
  let wroteRow = false
  for (const row of Array.from(table.querySelectorAll('tr'))) {
    const cells = Array.from(row.querySelectorAll('th, td'))
    if (cells.length === 0) continue
    if (wroteRow) paragraph.append(doc.createElement('br'))
    cells.forEach((cell, index) => {
      if (index > 0) paragraph.append(doc.createTextNode(CELL_SEPARATOR))
      while (cell.firstChild) paragraph.append(cell.firstChild)
    })
    wroteRow = true
  }
  if (wroteRow && (paragraph.textContent ?? '').trim().length > 0) {
    table.replaceWith(paragraph)
  } else {
    table.remove()
  }
}

/**
 * Flatten only the tables that are nested INSIDE another table, leaving
 * every outer table standing.
 *
 * For a surface that DOES have table nodes. The cell content model
 * deliberately excludes `table`, so a pasted nested table has nowhere
 * legal to land: ProseMirror would drop the inner table's elements and
 * strand its cell text as one unseparated run inside the outer cell.
 * Flattening it here keeps the inner grid readable as `a | b` text and
 * keeps the outer table's real structure.
 *
 * Innermost-first (the reverse of document order), so a table three deep
 * is already plain by the time its own parent is processed.
 */
export function flattenNestedTables(html: string): string {
  if (!html || !/<table/i.test(html)) return html
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const body = doc.body
  if (!body) return html
  const nested = Array.from(body.querySelectorAll('table table')).reverse()
  if (nested.length === 0) return html

  for (const table of nested) flattenOneTable(doc, table)
  return body.innerHTML
}

/**
 * Font stacks that mean "this text is code". Sources that render code
 * without semantic markup (Google Docs, Word, Outlook, some CMS
 * exports) express it as a monospace font on a `<span>` / `<font>`.
 */
const MONOSPACE_FAMILY =
  /monospace|courier|consolas|menlo|monaco|andale mono|lucida console|dejavu sans mono|liberation mono|roboto mono|source code pro|fira code|sf mono|ui-monospace/i

/**
 * Promote monospace-styled inline runs to real `<code>` elements so the
 * editor's `code` mark picks them up.
 *
 * Three guards keep this from over-firing:
 * - runs inside `pre` / `code` are skipped (syntax-highlight spans are
 *   monospace by definition, and are already in a code context);
 * - multi-line runs are skipped — those are a code BLOCK that lost its
 *   `<pre>`, and wrapping them in an inline mark would be wrong;
 * - a payload that is monospace END TO END is skipped entirely, because
 *   the font carries no contrast there and promoting every run would
 *   mark the whole paste as code.
 */
export function promoteMonospaceSpansToCode(html: string): string {
  if (!html || !/font-family|<font/i.test(html)) return html
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const body = doc.body
  if (!body) return html

  const candidates = Array.from(body.querySelectorAll('span[style], font[face]')).filter((el) => {
    if (el.closest('pre, code')) return false
    const declaration =
      el.getAttribute('face') ??
      /font-family\s*:\s*([^;]+)/i.exec(el.getAttribute('style') ?? '')?.[1] ??
      ''
    if (!MONOSPACE_FAMILY.test(declaration)) return false
    const text = el.textContent ?? ''
    return text.trim().length > 0 && !text.includes('\n')
  })
  if (candidates.length === 0) return html

  const promotedLength = candidates.reduce(
    (sum, el) => sum + (el.textContent ?? '').trim().length,
    0,
  )
  if (promotedLength >= (body.textContent ?? '').trim().length) return html

  for (const el of candidates) {
    const code = doc.createElement('code')
    code.textContent = el.textContent ?? ''
    el.replaceWith(code)
  }
  return body.innerHTML
}

/** What the receiving editor's schema can actually hold. */
export interface PasteSchemaCapabilities {
  hasTable: boolean
  hasHeading: boolean
}

/**
 * Rewrite a pasted HTML fragment to fit the receiving schema, BEFORE
 * ProseMirror parses it.
 *
 * Every rewrite is keyed on what the schema can hold rather than on a prop, so
 * it always reflects what this editor can actually store:
 *
 *   1. Sources that render code without semantic markup (Google Docs, Word,
 *      Outlook) express it as a monospace font on a `<span>` / `<font>`;
 *      promote those runs so the `code` mark picks them up.
 *   2. With no table nodes, flatten every table to legible rows — ProseMirror
 *      would otherwise keep the cell text and lose every cell boundary. With
 *      table nodes, flatten only the NESTED ones.
 *   3. With no heading nodes, demote headings to bold so the emphasis doesn't
 *      evaporate into an ordinary-looking line.
 *
 * Step 2's nested case is load-bearing in a way that is easy to misread:
 * leaving `table` out of the cell content expression does NOT make a nested
 * table impossible. ProseMirror fits content by finding ANY legal path, and a
 * cell may hold a `blockquote`, which may hold a table — so an unsanitised
 * nested table arrives wrapped in a stray blockquote rather than being
 * rejected. The sanitiser, not the schema, is what keeps tables un-nested.
 */
/**
 * Drop the blank paragraphs that hug a pasted cell's real content.
 *
 * Real document exporters pad cells with them — Confluence emits a
 * leading empty `<p>` in a table cell often enough that a pasted test
 * matrix routinely arrives with one in every cell of a column. They are
 * not harmless: an empty first paragraph pushes the cell's content down
 * and misaligns the whole row, and it reads as a stray line the author
 * did not type.
 *
 * Only the leading and trailing runs go. A blank paragraph BETWEEN two
 * blocks is left alone, because there it is plausibly deliberate
 * spacing, and a cell is never emptied — a cell whose entire content is
 * blank paragraphs keeps one, since the cell content model requires a
 * block and ProseMirror would synthesize one anyway.
 */
export function stripBlankEdgeParagraphsInCells(html: string): string {
  if (!html || !/<t[dh][\s>]/i.test(html)) return html
  const doc = new DOMParser().parseFromString(html, 'text/html')
  const body = doc.body
  if (!body) return html

  const isBlankParagraph = (el: Element): boolean => {
    if (el.tagName !== 'P') return false
    if (el.textContent?.trim()) return false
    // `<p><br></p>` is the same nothing, written differently. Anything
    // else with an element inside it (an image, an embed) is content.
    return Array.from(el.children).every((child) => child.tagName === 'BR')
  }

  let changed = false
  for (const cell of Array.from(body.querySelectorAll('td, th'))) {
    // Never strip the last block standing.
    while (cell.children.length > 1 && isBlankParagraph(cell.children[0] as Element)) {
      cell.children[0]?.remove()
      changed = true
    }
    while (
      cell.children.length > 1 &&
      isBlankParagraph(cell.children[cell.children.length - 1] as Element)
    ) {
      cell.children[cell.children.length - 1]?.remove()
      changed = true
    }
  }
  return changed ? body.innerHTML : html
}

export function transformPastedHtmlForSchema(
  html: string,
  capabilities: PasteSchemaCapabilities,
): string {
  let next = promoteMonospaceSpansToCode(html)
  if (capabilities.hasTable) {
    next = flattenNestedTables(next)
    next = stripBlankEdgeParagraphsInCells(next)
  } else {
    next = flattenTablesToParagraphs(next)
  }
  if (!capabilities.hasHeading) next = demoteHeadingsToBold(next)
  return next
}
